import { CalendarIcon, ChevronDownIcon } from 'lucide-react';
import type { DateRange } from 'react-day-picker';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useIsMobile } from '@/hooks/use-mobile';
import i18n from '@/utils/i18n';

type EventOverviewDateRangeProps = {
  value: DateRange | undefined;
  onChange: (range: DateRange | undefined) => void;
  onApply: () => void;
  loading: boolean;
};

/** Fulfillment-data style: outline trigger + popover calendar; Apply in footer (matches classic overview fetch semantics). */
export function EventOverviewDateRange({
  value,
  onChange,
  onApply,
  loading,
}: EventOverviewDateRangeProps) {
  const isMobile = useIsMobile();
  const canApply = Boolean(value?.from);

  return (
    <Popover modal={false}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="tw:justify-start tw:gap-2"
        >
          <CalendarIcon className="tw:size-4 tw:shrink-0" />
          <span className="tw:truncate">
            {value?.from
              ? `${value.from.toLocaleDateString(i18n.language)}${
                  value.to
                    ? ` – ${value.to.toLocaleDateString(i18n.language)}`
                    : ''
                }`
              : i18n.t('Event:dateRange')}
          </span>
          <ChevronDownIcon className="tw:size-4 tw:shrink-0 tw:opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={16}
        className="tw:w-auto tw:overflow-hidden tw:p-0"
      >
        <Calendar
          mode="range"
          defaultMonth={value?.from}
          selected={value}
          onSelect={onChange}
          numberOfMonths={isMobile ? 1 : 2}
        />
        <div className="tw:flex tw:justify-end tw:gap-2 tw:border-t tw:p-3 tw:bg-background">
          <Button
            type="button"
            size="sm"
            onClick={onApply}
            disabled={loading || !canApply}
          >
            {loading ? i18n.t('Common:loading') : i18n.t('Common:apply')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
